// react
import React, { useState } from 'react'
import papa from 'papaparse'
import { saveAs } from 'file-saver'

// components
import { Button, Input, Select } from '@st-operations/uikit/src/v2'
import Header from '_components/header'

// services
import { getPdpLiveRampReport } from '_services/audience'
import { logErrorAsync } from '_lib/utility'

// styles
import './styles.scss'

const RANGE = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  ALL: 'all',
  CUSTOM: 'custom',
}

const RANGE_OPTIONS = [
  { label: 'Last day', value: RANGE.DAY },
  { label: 'Last 7 days', value: RANGE.WEEK },
  { label: 'Last 30 days', value: RANGE.MONTH },
  { label: 'All time', value: RANGE.ALL },
  { label: 'Custom range', value: RANGE.CUSTOM },
]

// The endpoint reads start/end as inclusive UTC dates, so build them in UTC too.
const toUTCDate = (date) => date.toISOString().slice(0, 10)

const presetRange = (range) => {
  const start = new Date()
  const end = new Date()

  switch (range) {
    case RANGE.DAY:
      start.setUTCDate(start.getUTCDate() - 1)
      break
    case RANGE.WEEK:
      start.setUTCDate(start.getUTCDate() - 7)
      break
    case RANGE.MONTH:
      // A fixed 30 days, as the label says - not a calendar month, which is
      // 28 to 31 days depending on when it is picked.
      start.setUTCDate(start.getUTCDate() - 30)
      break
    default:
      // All time — both params omitted, which returns everything that qualifies.
      return { start: '', end: '' }
  }

  return { start: toUTCDate(start), end: toUTCDate(end) }
}

const PdpLiveRampReport = () => {
  const [range, setRange] = useState(RANGE.WEEK)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [downloading, setDownloading] = useState(false)

  const isCustom = range === RANGE.CUSTOM
  const { start, end } = isCustom ? { start: customStart, end: customEnd } : presetRange(range)

  const invalidCustomRange = isCustom && !!start && !!end && start > end
  const today = toUTCDate(new Date())

  const download = () => {
    setDownloading(true)

    getPdpLiveRampReport({ start, end })
      .then((csv) => {
        const { data } = papa.parse(csv.trim(), { header: true, skipEmptyLines: true })
        const suffix = start || end ? `${start || 'start'}_${end || today}` : 'all-time'

        saveAs(
          new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
          `pdp-liveramp-report-${suffix}.csv`
        )

        if (data.length) {
          toast(`Downloaded ${data.length} row${data.length === 1 ? '' : 's'}.`)
        } else {
          toast('No LiveRamp audiences in that range — the file has headers only.')
        }
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
      })
      .finally(() => setDownloading(false))
  }

  return (
    <div className="pdp-liveramp-report">
      <Header title="PDP LiveRamp Report" />

      <div className="pdp-liveramp-report__content">
        <p className="pdp-liveramp-report__description">
          Every LiveRamp audience created by PDP, one row per audience per LiveRamp destination,
          with the endpoint and rate the client agreed to. Filtered by audience creation date.
        </p>

        <div className="pdp-liveramp-report__controls">
          <Select
            className="pdp-liveramp-report__range"
            label="Date Range"
            options={RANGE_OPTIONS}
            value={RANGE_OPTIONS.find((option) => option.value === range)}
            onChange={(option) => setRange(option.value)}
            isSearchable={false}
            useReactSelect
          />

          <If condition={isCustom}>
            <Input
              className="pdp-liveramp-report__date"
              label="Start (UTC)"
              type="date"
              max={customEnd || today}
              value={customStart}
              onChange={setCustomStart}
            />
            <Input
              className="pdp-liveramp-report__date"
              label="End (UTC)"
              type="date"
              min={customStart}
              value={customEnd}
              onChange={setCustomEnd}
            />
          </If>
        </div>

        <p className="pdp-liveramp-report__summary">
          <Choose>
            <When condition={invalidCustomRange}>
              Start date must be on or before the end date.
            </When>
            <When condition={!start && !end}>Includes every qualifying audience, all time.</When>
            <When condition={start && !end}>
              Includes audiences created on or after {start} (UTC).
            </When>
            <When condition={!start && end}>
              Includes audiences created on or before {end} (UTC).
            </When>
            <Otherwise>
              Includes audiences created between {start} and {end} (UTC), inclusive.
            </Otherwise>
          </Choose>
        </p>

        <Button
          onClick={download}
          disabled={downloading || invalidCustomRange}
          isLoading={downloading}
        >
          Download CSV
        </Button>
      </div>
    </div>
  )
}

export default PdpLiveRampReport
